/**
 * Versão canónica e imutável de uma rota gerada (RouteSnapshot).
 *
 * Regras vinculativas:
 * - resumo (distância/deslocação) vem SEMPRE das mesmas pernas;
 * - orçamento total é SEMPRE a soma das categorias visíveis;
 * - alojamento só entra no orçamento quando o hotel foi mesmo escolhido;
 * - nada aqui usa `Math.random`, relógio, IO ou estado mutável.
 */

export type TravelLeg = {
  /** Identidade estável da perna dentro da rota. */
  legId?: string;
  /** Entradas ligadas por identidade lógica — nunca por índice. */
  fromEntryId?: string;
  toEntryId?: string;
  /** Modo pedido pela pessoa. */
  requestedMode?: string;
  /** Modo realmente usado pelo fornecedor (`null` quando não há). */
  resolvedMode?: string | null;
  /** `ok` | `unavailable` | `pending`. */
  status?: "ok" | "unavailable" | "pending";
  /** Distância da perna em metros (0 quando desconhecida). */
  distanceMeters: number;
  /** Duração da perna em minutos (0 quando desconhecida). */
  durationMinutes: number;
};


export type TravelSummary = {
  legCount: number;
  totalDistanceMeters: number;
  totalTravelMinutes: number;
  /** Fonte única das duas métricas; "none" quando não há pernas válidas. */
  source: "travelLegs" | "none";
};

/**
 * Resumo a partir de UMA lista de pernas. Uma perna só conta quando tem
 * distância e duração utilizáveis — nunca "53 m" com "18h de deslocação".
 */
export function summarizeTravelLegs(legs: readonly TravelLeg[]): TravelSummary {

  const usable = legs.filter(
    (l) =>
      l.status !== "unavailable" &&
      l.status !== "pending" &&
      Number.isFinite(l.distanceMeters) &&
      Number.isFinite(l.durationMinutes) &&
      l.distanceMeters > 0 &&
      l.durationMinutes > 0,
  );

  if (usable.length === 0) {
    return { legCount: 0, totalDistanceMeters: 0, totalTravelMinutes: 0, source: "none" };
  }
  return {
    legCount: usable.length,
    totalDistanceMeters: Math.round(usable.reduce((a, l) => a + l.distanceMeters, 0)),
    totalTravelMinutes: Math.round(usable.reduce((a, l) => a + l.durationMinutes, 0)),
    source: "travelLegs",
  };
}

export type BudgetCategoryKey = "attractions" | "meals" | "transport" | "accommodation";

export type BudgetCategory = { key: BudgetCategoryKey; amount: number };

export type RouteBudget = {
  categories: BudgetCategory[];
  total: number;
  accommodationIncluded: boolean;
};

/**
 * Orçamento explicável: o total é literalmente a soma das categorias visíveis.
 * `accommodationSelected` só é aceite com hotel realmente escolhido.
 */
export function buildBudget(input: {
  attractions: number;
  meals: number;
  transport: number;
  accommodationSelected?: number;
  hotelSelected?: boolean;
}): RouteBudget {
  const round2 = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
  const categories: BudgetCategory[] = [
    { key: "attractions", amount: round2(input.attractions) },
    { key: "meals", amount: round2(input.meals) },
    { key: "transport", amount: round2(input.transport) },
  ];
  const accommodationIncluded = Boolean(input.hotelSelected) && (input.accommodationSelected ?? 0) > 0;
  if (accommodationIncluded) {
    categories.push({ key: "accommodation", amount: round2(input.accommodationSelected ?? 0) });
  }
  return {
    categories,
    total: round2(categories.reduce((a, c) => a + c.amount, 0)),
    accommodationIncluded,
  };
}

/** Total ativo do itinerário: soma das janelas por dia, nunca fim menos início. */
export function activeItineraryMinutes(
  days: readonly { startMinutes: number; endMinutes: number }[],
): number {
  return days.reduce((sum, d) => sum + Math.max(0, Math.round(d.endMinutes - d.startMinutes)), 0);
}

export type RouteSnapshot = {
  routeRevision: number;
  generationRequestId?: string;
  travelLegs: TravelLeg[];
  summary: TravelSummary;
  budget: RouteBudget;
};

export function buildRouteSnapshot(input: {
  routeRevision: number;
  generationRequestId?: string;
  travelLegs: readonly TravelLeg[];
  budget: RouteBudget;
}): RouteSnapshot {
  // Cópias defensivas: pernas e orçamento publicados não podem ser alterados
  // por mutações tardias nas estruturas privadas do rascunho.
  return {
    routeRevision: input.routeRevision,
    generationRequestId: input.generationRequestId,
    travelLegs: Object.freeze(input.travelLegs.map((leg) => Object.freeze({ ...leg }))) as TravelLeg[],
    summary: summarizeTravelLegs(input.travelLegs),
    budget: Object.freeze({
      ...input.budget,
      categories: Object.freeze(
        input.budget.categories.map((category) => Object.freeze({ ...category })),
      ) as RouteBudget["categories"],
    }) as RouteBudget,
  };

}

/** Barreira final: nunca mostrar valores incoerentes. */
export function snapshotConsistencyPassed(snapshot: RouteSnapshot): boolean {
  const recomputed = summarizeTravelLegs(snapshot.travelLegs);
  if (recomputed.totalDistanceMeters !== snapshot.summary.totalDistanceMeters) return false;
  if (recomputed.totalTravelMinutes !== snapshot.summary.totalTravelMinutes) return false;
  const sum =
    Math.round(snapshot.budget.categories.reduce((a, c) => a + c.amount, 0) * 100) / 100;
  return sum === snapshot.budget.total;
}

/** Resposta assíncrona antiga nunca sobrescreve a rota atual. */
export function isStaleGenerationResult(
  current: { routeRevision: number; generationRequestId?: string },
  incoming: { routeRevision: number; generationRequestId?: string },
): boolean {
  if (incoming.routeRevision < current.routeRevision) return true;
  if (
    current.generationRequestId &&
    incoming.generationRequestId &&
    incoming.generationRequestId !== current.generationRequestId &&
    incoming.routeRevision <= current.routeRevision
  ) {
    return true;
  }
  return false;
}

/** Trace sanitizado — sem texto do pedido, locais, horas ou datas. */
export function routeSnapshotTrace(
  snapshot: RouteSnapshot,
  extra: { mealCandidatesValidated: number; staleAsyncResultDiscarded: number },
): Record<string, string | number | boolean> {
  return {
    routeRevision: snapshot.routeRevision,
    summaryRevision: snapshot.routeRevision,
    budgetRevision: snapshot.routeRevision,
    travelLegCount: snapshot.summary.legCount,
    travelDistanceSource: snapshot.summary.source,
    travelDurationSource: snapshot.summary.source,
    budgetCategoryCount: snapshot.budget.categories.length,
    accommodationIncluded: snapshot.budget.accommodationIncluded,
    mealCandidatesValidated: extra.mealCandidatesValidated,
    staleAsyncResultDiscarded: extra.staleAsyncResultDiscarded,
    snapshotConsistencyPassed: snapshotConsistencyPassed(snapshot),
  };
}
